import {
  NEW_PLAN_ID,
  PLAN_TYPE_DESTINATION,
  PLAN_TYPE_SOURCE,
} from '../CreateRecommend/RecommendSourceDestination/constants';
import {
  nonEmptyStringOrNull,
  updateRecsVarsProps,
  updateRecommendPotsVariables,
  getPotContactId,
  getIsMinor,
} from './helpers';
import {
  OVRecommendActionEnum,
  BankDetailsEnum,
  GenerateOVRPotBatchArguments,
} from '../../../schema-types/globalTypes';

import FREQUENCY from '../../../constants/Frequency';
import {UpsertOvrecommendVariables} from '../../../schema-types/UpsertOvrecommend';
import {RECOMMENDATION_SUBTYPE_TO_SERVER} from '../../../constants/SolutionType';
import OWNERSHIP from '../../../constants/Ownership';
import {getISODateOrUndefined} from '../../../utils/getISODate';
import {toUUID, toUUIDOrNull} from '../../../graphql/uuid';
import validateUUID from '../../../utils/validateUUID';

function getAmount(amount?: string | number) {
  try {
    if (amount) {
      const amountNum = Number(amount);
      if (amountNum) {
        return amountNum;
      }
    }
    return undefined;
  } catch (err) {
    return undefined;
  }
}

const getWithdrawalVariables = async ({
  accountId,
  goalId,
  ovrecommendId,
  values,

  recommendDetails,
  contacts = [],
}: // bankAccountProductNameId,
updateRecsVarsProps): Promise<UpsertOvrecommendVariables> => {
  const {withdraw, contact, urgency, urgencyReason} = values;
  const isMinor = getIsMinor(contact, contacts);

  if (!withdraw?.destination) {
    throw Error('Must have a destination pot');
  }

  const isNewPlanRequired = withdraw?.destination?.id === NEW_PLAN_ID;

  const withdrawVariables: UpsertOvrecommendVariables = {
    action: OVRecommendActionEnum.WITHDRAW,
    ovrecommendId: validateUUID(ovrecommendId)!,
    accountId: validateUUID(accountId)!,
    goalId: validateUUID(goalId)!,
    isNewPlanRequired,
    contactId: getPotContactId(contact, isMinor),
    withdrawalReason: nonEmptyStringOrNull(
      withdraw?.source?.reason || recommendDetails?.withdrawalReason
    ),
    // withdrawalAmount: getCurrencyFloat(
    //   withdraw?.source?.singleClientAmount || recommendDetails?.withdrawalAmount
    // ),
    solutionType: RECOMMENDATION_SUBTYPE_TO_SERVER.PRODUCT_BASED,
    // contributionFrequency: frequency,
    urgency,
    urgencyReason,
    // startDate MUST BE an ISO string
    // https://the-guild.dev/graphql/scalars/docs/scalars/date-time
    startDate:
      getISODateOrUndefined(withdraw?.source?.start) ||
      getISODateOrUndefined(recommendDetails?.startDate),
    endDate:
      getISODateOrUndefined(withdraw?.source?.end) ||
      getISODateOrUndefined(recommendDetails?.endDate),
  };

  // debugger;
  return withdrawVariables;
};

export const generateWithdrawalOVRPots = async ({
  accountId,
  ovrecommendId,
  values,
  generateRecommendationPots,
  reviewId,
  contacts = [],
}: updateRecommendPotsVariables) => {
  const potArgs: GenerateOVRPotBatchArguments[] = [];
  const {withdraw, contact, urgency, urgencyReason} = values;
  const isMinor = getIsMinor(contact, contacts);

  if (!withdraw?.destination) {
    throw Error('Must have a destination pot');
  }

  const isNewPlanRequired = withdraw?.destination?.id === NEW_PLAN_ID;
  const frequency = values.withdraw?.source?.frequency || FREQUENCY.SINGLE;
  const hasSingle = frequency === FREQUENCY.SINGLE;
  const hasRecurring = frequency !== FREQUENCY.SINGLE;

  const sourcePotVariables: GenerateOVRPotBatchArguments = {
    planType: PLAN_TYPE_SOURCE,
    potId: toUUIDOrNull(withdraw?.source?.id),
    singleClientAmount: hasSingle
      ? getAmount(withdraw?.source?.singleClientAmount)
      : undefined,
    recurringClientAmount: hasRecurring
      ? getAmount(withdraw?.source.recurringClientAmount)
      : undefined,
    recurringClientFrequency: frequency,
    full: withdraw?.source.full,
  };
  potArgs.push(sourcePotVariables);

  if (isNewPlanRequired) {
    const destination = withdraw.destination!;
    const newDestinationPotId = destination.newExistingPotId;

    const newDestinationVariables: GenerateOVRPotBatchArguments = {
      planType: PLAN_TYPE_DESTINATION,
      potId: toUUIDOrNull(newDestinationPotId),
      singleClientAmount: hasSingle
        ? getAmount(withdraw?.source?.singleClientAmount)
        : undefined,
      recurringClientAmount: hasRecurring
        ? getAmount(withdraw?.source?.recurringClientAmount)
        : undefined,
      recurringClientFrequency: frequency,
      productProviderId: destination.productProvider,
      isDummy: false,
      reviewId: toUUID(reviewId),
      planNumber:
        destination.bankDetails === BankDetailsEnum.NOT_KNOWN
          ? null
          : destination.accountNumber,
      sortCode:
        destination.bankDetails === BankDetailsEnum.NOT_KNOWN
          ? null
          : destination.sortCode?.replaceAll('-', ''),
      policyHolder: destination.accountName,
      isBank: true,
      originalOvrId: toUUID(ovrecommendId),
      // productNameId: bankAccountProductNameId,
      contactId:
        destination.contactId === OWNERSHIP.JOINT_CODE && !isMinor
          ? null
          : toUUIDOrNull(destination.contactId),
      bankDetails: destination.bankDetails,
    };
    potArgs.push(newDestinationVariables);
  } else if (withdraw?.destination?.id) {
    potArgs.push({
      potId: toUUID(withdraw?.destination?.id),
      planType: PLAN_TYPE_DESTINATION,
      singleClientAmount: hasSingle
        ? getAmount(withdraw?.source?.singleClientAmount)
        : undefined,
      recurringClientAmount: hasRecurring
        ? getAmount(withdraw?.source?.recurringClientAmount)
        : undefined,
      recurringClientFrequency: frequency,
    });
  }
  await generateRecommendationPots({
    variables: {
      accountId: validateUUID(accountId)!,
      ovrecommendId: validateUUID(ovrecommendId)!,
      args: potArgs,
    },
  });
};

export default getWithdrawalVariables;
